import React, { Suspense, useEffect, useMemo } from "react";
import { Route } from "react-router-dom";
import { IonApp, IonRouterOutlet, IonSpinner, setupIonicReact, useIonToast } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Switch, useHistory, useLocation } from "react-router";
import config from "./settings/config";
import "./pages/InitialConfiguration/InitialConfiguration.min.css";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { TextZoom } from "@capacitor/text-zoom";
import { Keyboard, KeyboardResize } from "@capacitor/keyboard";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Badge } from "@capawesome/capacitor-badge";
import { App as CapacitorApp } from "@capacitor/app";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { bootStep } from "./services/diag";

// ── Keep static: pre-login, framework, always-mounted components ──────
import PreLoginPage from "./pages/PreLogin";
import LoginPage from "./pages/Login";
import steps from "./pages/InitialConfiguration";
import GetTokenPage from "./pages/Login/getToken";
import VerifyEmailLinkPage from "./pages/VerifyEmailLink";
import GetTokenAdminPage from "./pages/Login/getToken/GetTokenAdmin";
import MainMenu from "./components/mainMenu";
import RedirectToHome from "./components/RedirectToHome";
import ErrorBoundary from "./pages/ErrorBoundary";
import AppStateManager from "./components/AppStateManager";
import AuthBootstrap from "./services/AuthBootstrap";
import RouteAuth from "./services/RouteAuth";
import CoinBalancePoller from "./components/CoinBalancePoller";
import CoinTransactionToast from "./components/CoinTransactionToast";
import RecipeVideoOverlay from "./components/overlays/RecipeVideoOverlay";
import NotificationPopup from "./components/modals/NotificationPopup";
import CommentsSheet from "./components/CommentsSheet";
import KeyboardAvoidanceManager from "./components/KeyboardAvoidanceManager";
import {
  PostCommentsDeepLink,
  CommentThreadDeepLink,
} from "./components/CommentsSheet/CommentsDeepLink";
import ForceUpdateGate from "./components/ForceUpdateGate";
import RealEmailGate from "./components/RealEmailGate";
import AppleRecoverGate from "./components/AppleRecoverGate";
import PermissionGate from "./components/PermissionGate";
import PushBanner from "./components/PushBanner";
import StreamChatBootstrap from "./components/StreamChatBootstrap";
import pushNotificationService, {
  consumePendingPushNavigation,
} from "./services/pushNotifications";
import PremiumTransitionWatcher from "./components/PremiumTransitionWatcher/PremiumTransitionWatcher";
import PremiumLiteJourneyIntro from "./components/PremiumLiteJourneyIntro/PremiumLiteJourneyIntro";
import EntitlementPromptHost from "./components/EntitlementPromptHost";
import CommunityDeepLinkHandler from "./components/CommunityDeepLinkHandler";
import StoryPromptSheet from "./components/Stories/StoryPromptSheet";
import {
  recordStorePurchaseConsent,
  takePurchaseRefusalMessage,
} from "./services/purchaseConsents";
import {
  LegacyPaymentRedirect,
  LegacyProductsRedirect,
} from "./components/LegacyCheckoutRedirect";
import AnalyticsScreenTracker from "./components/AnalyticsScreenTracker";
import ActivityPingTracker from "./components/ActivityPingTracker";
import {
  trackFirstOpenOnce,
  startFunnelFlushListeners,
} from "./services/funnelTracker";
import { setVideoTelemetryUser, startVideoTelemetry } from "./services/videoTelemetry";
import ImpersonationBanner from "./components/ImpersonationBanner";
import { useDispatch, useSelector } from "react-redux";
import {
  incrementUnreadQuantity,
  refreshProfileAfterActivation,
} from "./redux/actions";
import { resetUserInfoThrottle } from "./redux/actions/userActions";
import { getEntitlements } from "./redux/actions/subscriptionsActions";
import { markPremiumOptimistic } from "./redux/reducer/userSlice";
import { planForStoreProductId } from "./utils/memberPlan";
import { safeNavigate } from "./utils/navigationHelper";
import { clearCalendarCache } from "./services/calendarCache";
import { invalidateTrialInfoCache } from "./services/trialInfoCache";
import {
  ENTITLEMENT_ID,
  findPackageBySku,
  getActiveProductIdentifier,
  getAllOfferings,
  hasActiveMasterClubPremium,
  initRevenueCat,
  purchasePackage,
  reconcilePurchaseWithBackend,
} from "./services/RevenueCat";
import { getAuthToken } from "./services/authStorage";

// ── Lazy-loaded pages ─────────────────────────────────────────────────
const SocialProfilePage = React.lazy(() => import("./pages/SocialProfile"));
const FollowersPage = React.lazy(() => import("./pages/Followers"));
const FollowingPage = React.lazy(() => import("./pages/Following"));
const LessonsListPage = React.lazy(() => import("./pages/Course/LessonsList/index.js"));
const CourseResumePage = React.lazy(() => import("./pages/Course/Resume"));
const ProfileSettingsPage = React.lazy(() => import("./pages/ProfileSettings/index.js"));
const BlockedUsersPage = React.lazy(() => import("./pages/BlockedUsers/index.js"));
const MioPercorsoStoricoPage = React.lazy(() => import("./pages/MioPercorsoStorico"));
const HomePage = React.lazy(() => import("./pages/home/index.js"));
const GroupsPage = React.lazy(() => import("./pages/Groups"));
const GroupPage = React.lazy(() => import("./pages/Groups/Group"));
const ChatPage = React.lazy(() => import("./pages/Chat"));
const LogoutRoute = React.lazy(() => import("./components/Logout"));
const MultimediaPage = React.lazy(() => import("./pages/Multimedia"));
// const MultimediaGroupPage = React.lazy(() => import("./pages/MultimediaGroup")); // Removed — replaced by new Videos module
const NotificationsPage = React.lazy(() => import("./pages/Notifications"));
const NotificationsConfigPage = React.lazy(() => import("./pages/NotificationsConfig"));
const ModuleIntroPage = React.lazy(() => import("./pages/Course/ModuleIntro/index.js"));
const DiaryPage = React.lazy(() => import("./pages/Diary"));
const PrimaDopoCreatePage = React.lazy(() => import("./pages/PrimaDopoCreate"));
const DiaryMeasurePage = React.lazy(() => import("./pages/DiaryMeasure"));
const RecipesPage = React.lazy(() => import("./pages/Recipes"));
const RecipePage = React.lazy(() => import("./pages/Recipes/Recipe"));
const MenuPage = React.lazy(() => import("./pages/Menu"));
const RecipeMenuPage = React.lazy(() => import("./pages/Menu/RecipeMenu"));
const ProgressPage = React.lazy(() => import("./pages/Progress"));
const ProfilePage = React.lazy(() => import("./pages/Profile"));
const PaymentsPage = React.lazy(() => import("./pages/Payments"));
const ShopPage = React.lazy(() => import("./pages/Shop/index.js"));
const AddPostPage = React.lazy(() => import("./pages/Post/AddPost"));
const NotFoundPage = React.lazy(() => import("./pages/404"));
const PaymentSuccessPage = React.lazy(() => import("./pages/Payments/PaymentSuccessPage"));
const PaymentProcessingPage = React.lazy(() => import("./pages/Payments/PaymentProcessingPage"));
const PaymentMethodProcessingPage = React.lazy(() => import("./pages/Payments/PaymentMethodProcessingPage"));
const ScreenlockPage = React.lazy(() => import("./pages/Screenlock"));
const StartFreeRoutePage = React.lazy(() => import("./pages/StartFreeRoute"));
const CibiNonGraditiPage = React.lazy(() => import("./pages/CibiNonGraditi"));
const WhatIsPage = React.lazy(() => import("./pages/Help/WhatIs"));
const HowToUsePage = React.lazy(() => import("./pages/Help/HowToUse"));
const HowTrialWorksPage = React.lazy(() => import("./pages/Help/HowTrialWorks"));
const LessonPage = React.lazy(() => import("./pages/Course/Lesson"));
const CourseOverviewPage = React.lazy(() => import("./pages/Course"));
const CourseTabsPage = React.lazy(() => import("./pages/Course/TabsPage"));
const HomeDashboardPage = React.lazy(() => import("./pages/HomeDashboard"));
const CoinsBalancePage = React.lazy(() => import("./pages/CoinsBalance"));
const CoinsHistoryPage = React.lazy(() => import("./pages/CoinsHistory"));
const CoinsRulesPage = React.lazy(() => import("./pages/CoinsRules"));
const SubstitutionPage = React.lazy(() => import("./pages/Substitution/index.js"));
const WeHaveFinished = React.lazy(() => import("./pages/InitialConfiguration/steps/WeHaveFinished.js"));
const StreamingEventDetailPage = React.lazy(() => import("./pages/Streaming/StreamingEventDetail"));
const StreamingReplayPage = React.lazy(() => import("./pages/Streaming/StreamingReplay"));
const VideoDetailPage = React.lazy(() => import("./pages/Videos/VideoDetail"));
const VideoCategoryPage = React.lazy(() => import("./pages/Videos/VideoCategory"));
const VideoPreferitiPage = React.lazy(() => import("./pages/Videos/VideoPreferiti"));
const ChallengesPage = React.lazy(() => import("./pages/Challenges"));
const PaywallPage = React.lazy(() => import("./pages/Paywall"));
const LegalPage = React.lazy(() => import("./pages/Legal"));
const AreaTeamPostsPage = React.lazy(() => import("./pages/AreaTeam/Posts"));
const AreaTeamCreatePostsPage = React.lazy(() => import("./pages/AreaTeam/CreatePosts"));
const AreaTeamGestioneUtentiPage = React.lazy(() => import("./pages/AreaTeam/GestioneUtenti"));
const AreaTeamChatSupportoPage = React.lazy(() => import("./pages/AreaTeam/ChatSupporto"));
const AreaTeamChatSupportoDetailPage = React.lazy(() => import("./pages/AreaTeam/ChatSupporto/Detail"));
const AreaTeamPercorsoFreePage = React.lazy(() => import("./pages/AreaTeam/PercorsoFree"));

// Suspense fallback — centered spinner
const LazyFallback = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <IonSpinner name="crescent" />
  </div>
);

setupIonicReact({
  swipeGesture: false,
  swipeBackEnabled: false,
  // mode: 'ios',
});

// Handles navigation triggered by notification taps. MUST live inside
// <IonReactRouter> so useHistory() resolves — the App component itself renders
// the Router, so it sits outside the Router context and its history is undefined.
const PushNavigationHandler = () => {
  const history = useHistory();

  useEffect(() => {
    const onPushNavigate = (e) => {
      const path = e?.detail?.path;
      if (typeof path === "string") {
        // Clear the cold-start buffer so the mount-time replay can't double-fire.
        consumePendingPushNavigation();
        history.push(path);
      }
    };
    window.addEventListener("push:navigate", onPushNavigate);

    // Premium Lite Phase 4: «Il mio abbonamento» in the Shop asks the app to
    // change a store plan; that happens in the paywall. Leaving the Shop goes
    // through safeNavigate like the footer: while the widget is mounted it turns
    // every push into /shopping-widget/… (HistoryTransitions), so a plain push
    // landed on /shopping-widget/paywall and lost ?piano=lite.
    const onOpenPaywall = (e) => {
      const lite = e?.detail?.plan === "premium_lite";
      safeNavigate(history, lite ? "/paywall?piano=lite" : "/paywall", window.location.pathname);
    };
    window.addEventListener("shopwidgetopenpaywall", onOpenPaywall);

    // Cold start (app killed → notification tap): the tap may have fired before
    // this listener existed, so its CustomEvent was lost. Replay the buffered path.
    const pending = consumePendingPushNavigation();
    if (pending) history.push(pending);

    return () => {
      window.removeEventListener("push:navigate", onPushNavigate);
      window.removeEventListener("shopwidgetopenpaywall", onOpenPaywall);
    };
  }, [history]);

  return null;
};

const App = () => {
  const TOTAL_STEPS = 5;

  const stepRoutes = useMemo(() => {
    return steps.map((StepComp, idx) => {
      const step = idx + 1;

      function WrappedStep(props) {
        return <StepComp {...props} step={step} totalSteps={TOTAL_STEPS} />;
      }
      WrappedStep.displayName = `InitialConfigStep${step}`;

      return { step, WrappedStep };
    });
  }, []);

  const history = useHistory();
  const dispatch = useDispatch();
  const [presentToast] = useIonToast();
  const userId = useSelector((s) => s.user?.user?.id);
  // Drives the app-icon badge (see the effect below).
  const recipientsUnreadCount = useSelector(
    (s) => s.notifications?.recipientsUnreadCount ?? 0,
  );

  // Personal notifications (new comment / @mention / follow request) arriving in
  // the foreground show a brief top banner instead of the full central popup;
  // tapping it deep-links (comment → the post in Community with the comment
  // focused, follow request → your own Contatti > Follower list). Editorial
  // pushes still use the popup (handled in pushNotifications.js).
  //
  // <PushBanner /> below owns this end to end — it listens for `push:banner`,
  // renders the actor's avatar and navigates on tap. It replaced an Ionic toast
  // that could only show a line of text, so a notification arrived with no
  // indication of who it was from.

  // DIAG: the React tree mounted, so boot got past render.
  useEffect(() => {
    bootStep("app_mounted");
  }, []);

  // First-party funnel tracking (see services/funnelTracker.js). Mounted here
  // rather than inside a router component so the very first launch is recorded
  // even if the app opens straight onto a deep link. `trackFirstOpenOnce` is
  // idempotent per install: it fires `app_first_open` only when it had to mint
  // the device id, which by definition is the first launch.
  useEffect(() => {
    trackFirstOpenOnce();
    startFunnelFlushListeners();
  }, []);

  // Course video playback telemetry (services/videoTelemetry.js): finish the
  // sessions a closed app left open, and keep it pointed at whoever is signed
  // in — records are only ever sent for that member.
  useEffect(() => {
    startVideoTelemetry();
  }, []);

  useEffect(() => {
    setVideoTelemetryUser(userId);
  }, [userId]);

  // Tell Capgo the OTA bundle booted successfully. Without this call, the
  // updater assumes the freshly-applied bundle failed and rolls back to the
  // previous one on the next launch. Runs once, early; harmless no-op on web.
  useEffect(() => {
    CapacitorUpdater.notifyAppReady().catch(() => {});
  }, []);

  // Dismiss the native splash screen once the web app has mounted. The splash is
  // configured with launchAutoHide:false so the branded launch image stays up
  // through the webview boot (instead of flashing to a white loading screen);
  // this hides it after the first content has had a moment to paint. A hard
  // fallback still hides it if anything downstream stalls.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    const hide = () => SplashScreen.hide().catch(() => {});
    const t = setTimeout(hide, 600);
    return () => clearTimeout(t);
  }, []);

  // Lock the whole app to portrait on native. The only place landscape is
  // allowed is full-screen video, where CustomVimeoPlayer unlocks on entry and
  // re-locks portrait on exit. (iOS Info.plist keeps landscape enabled so the
  // player can still rotate; we enforce portrait everywhere else here.)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    ScreenOrientation.lock({ orientation: "portrait" }).catch(() => {});
  }, []);

  useEffect(() => {
    // Only apply inside native WebView (Android/iOS), not on web
    if (Capacitor.getPlatform() !== "web") {
      TextZoom.set({ value: 1.0 }).catch(() => {});
    }
  }, []);

  // Keep the phone's own status bar readable: dark clock/battery/signal over the
  // light bar background set above.
  //
  // Style.Light means "dark content for a light background" — the naming refers
  // to the background, not the icons. Without it the system default follows the
  // device theme, so a phone in dark mode drew white icons on #f7f7f7 and the
  // status bar looked switched off.
  //
  // Re-applied on resume because full-screen playback and orientation changes
  // hand the bar back in whatever state the system chose.
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    const apply = () => {
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
      StatusBar.show().catch(() => {});
    };

    apply();

    let remove;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) apply();
    })
      .then((handle) => {
        remove = () => handle.remove();
      })
      .catch(() => {});

    return () => remove?.();
  }, []);

  // Mirror the unread count onto the app icon.
  //
  // Nothing in the app ever set or cleared the icon badge: the number came from
  // whatever push last carried one (Stream Chat's APNs payload does), and iOS
  // keeps a badge until an app explicitly sets it to zero. So a single chat push
  // left a permanent "1" on the icon with nothing unread behind it.
  //
  // `recipientsUnreadCount` is the same figure the in-app bell shows, so the two
  // now agree. Re-applied on resume because the count can move while the app is
  // in the background — that is exactly when a push arrives, or when the member
  // reads the notification on another device.
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;

    const apply = () => {
      const count = Number(recipientsUnreadCount) || 0;

      // clear() rather than set({count: 0}): on iOS a zero badge is what removes
      // the bubble, and the plugin's own clear is the supported way to ask.
      const task = count > 0 ? Badge.set({ count }) : Badge.clear();
      task?.catch?.(() => {});
    };

    apply();

    let remove;
    CapacitorApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) apply();
    })
      .then((handle) => {
        remove = () => handle.remove();
      })
      .catch(() => {});

    return () => remove?.();
  }, [recipientsUnreadCount]);

  // Pin the iOS keyboard resize mode to "native" so the WebView viewport shrinks
  // when the keyboard appears. This keeps fixed-bottom composers (comments,
  // paywall/auth inputs) above the keyboard automatically — the comment sheet
  // therefore must NOT also translate its footer, or it double-compensates and
  // sits too high. Making it explicit avoids relying on the plugin default.
  useEffect(() => {
    if (Capacitor.getPlatform() !== "ios") return;
    Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
  }, []);

  // Ensure native push listeners are registered at startup regardless of which
  // route the app launches into (a cold start from a notification tap may not
  // pass through HomeDashboard, where init normally happens). Only runs when a
  // session already exists and permission is granted — never prompts here.
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return;
    if (!getAuthToken()) return;
    pushNotificationService.initialize({ prompt: false }).catch(() => {});
  }, []);

  // Listen for shop widget purchase completion (e.g. guest → subscriber)
  // Updates user state so profile icon and subscription-dependent UI reflect the new status
  useEffect(() => {
    const onShopWidgetNewPurchase = () => {
      // Non-IAP purchase: the activation webhook is async, so poll the profile
      // until the subscription flips (then getUserInfo unlocks the gates in
      // place — no logout needed).
      dispatch(refreshProfileAfterActivation());
      clearCalendarCache();
      invalidateTrialInfoCache();
    };
    globalThis.addEventListener("shopwidgetnewpurchase", onShopWidgetNewPurchase);
    return () =>
      globalThis.removeEventListener(
        "shopwidgetnewpurchase",
        onShopWidgetNewPurchase,
      );
  }, [dispatch]);

  // Bridge shop-widget digital-product clicks to RevenueCat on native.
  // The widget emits this when the user taps "Acquista ora" on a product the
  // backend marked `isIap: true` (i.e. native channel, digital productClass,
  // channel.iap flag on). We translate `pgProductId` (store SKU) into an RC
  // Package by walking every offering, then drive Purchases.purchasePackage
  // and reuse the existing post-purchase Redux refresh path on success.
  useEffect(() => {
    if (Capacitor.getPlatform() === "web") return undefined;

    const onDigitalPurchaseClicked = async (event) => {
      const detail = event?.detail ?? {};
      const pgProductId = detail.pgProductId ?? null;
      const pgPriceId = detail.pgPriceId ?? null;

      console.log("[ShopWidget IAP] event received", { pgProductId, pgPriceId, userId });

      if (!pgProductId) {
        console.warn("[ShopWidget IAP] missing pgProductId in event detail; aborting");
        presentToast({ message: "Prodotto non disponibile per l'acquisto.", duration: 3000 });
        return;
      }

      // initRevenueCat is idempotent. We re-call here so the bridge works even
      // if the user reaches the shop before AuthBootstrap finishes RC init on
      // cold start.
      if (userId != null) {
        console.log("[ShopWidget IAP] ensuring RC init for user", userId);
        await initRevenueCat(userId);
      } else {
        console.warn("[ShopWidget IAP] no userId in redux yet — purchase may fail");
      }

      console.log("[ShopWidget IAP] fetching offerings");
      const offerings = await getAllOfferings();
      if (!offerings) {
        console.error("[ShopWidget IAP] getAllOfferings returned null");
        presentToast({
          message: "Impossibile caricare i prodotti dallo store. Riprova.",
          duration: 3000,
        });
        return;
      }

      // Diagnostic: dump every package RC reports so we can verify our SKUs
      // are picked up before flipping the env flags in production.
      const allPackages = Object.values(offerings.all ?? {}).flatMap((o) => ({
        offering: o?.identifier,
        packages: (o?.availablePackages ?? []).map((p) => ({
          identifier: p?.identifier,
          packageType: p?.packageType,
          productIdentifier: p?.product?.identifier,
          price: p?.product?.priceString,
        })),
      }));
      console.log("[ShopWidget IAP] offerings snapshot", {
        current: offerings.current?.identifier ?? null,
        all: allPackages,
      });

      const match = findPackageBySku(offerings, pgProductId);
      if (!match) {
        console.error("[ShopWidget IAP] no RC package matches SKU", pgProductId);
        presentToast({
          message:
            "Prodotto non disponibile su questo dispositivo. Contatta l'assistenza.",
          duration: 3000,
        });
        return;
      }

      console.log("[ShopWidget IAP] matched package", {
        sku: pgProductId,
        offering: match.offeringIdentifier,
        packageIdentifier: match.pkg?.identifier,
        productIdentifier: match.pkg?.product?.identifier,
      });

      // Premium Lite Phase 4: the other plan over a running one is a plan
      // change. Google must replace the current subscription (or the member
      // pays for two), and a downgrade waits for the renewal: the paywall
      // handles both, never this direct purchase.
      const activeSku = await getActiveProductIdentifier();
      const wantedPlan = planForStoreProductId(pgProductId);
      if (activeSku && planForStoreProductId(activeSku) !== wantedPlan) {
        window.dispatchEvent(
          new CustomEvent("shopwidgetopenpaywall", { detail: { plan: wantedPlan } }),
        );
        return;
      }

      // The Shop shows the Termini e Condizioni sentence under the button:
      // tapping it is the acceptance, straight to the store sheet (client,
      // 05/11/2026). Its proof goes first: no store sheet without it.
      const consentKept = await recordStorePurchaseConsent(
        match.pkg?.product?.identifier ?? pgProductId,
      );
      if (!consentKept) {
        presentToast({
          message:
            takePurchaseRefusalMessage() ??
            "Non è stato possibile registrare la conferma. Riprova.",
          duration: 5000,
        });
        return;
      }

      try {
        const result = await purchasePackage(match.pkg);

        if (result.cancelled) {
          console.log("[ShopWidget IAP] user cancelled purchase");
          presentToast({ message: "Pagamento annullato.", duration: 2000 });
          return;
        }

        console.log("[ShopWidget IAP] purchase succeeded", {
          activeEntitlements: Object.keys(result.customerInfo?.entitlements?.active ?? {}),
          nonSubscriptionCount: (result.customerInfo?.nonSubscriptionTransactions ?? []).length,
        });

        // If the purchased product granted master_club_premium, mirror the
        // Paywall's optimistic flip so the side menu / premium UI flips
        // immediately. Harmless no-op for one-shot digital products that
        // don't grant an entitlement.
        const entitlement =
          result.customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
        if (entitlement && hasActiveMasterClubPremium(result.customerInfo)) {
          console.log("[ShopWidget IAP] applying optimistic premium flip");
          // Premium Lite shares the entitlement: the SKU bought says which plan.
          const targetPlan = planForStoreProductId(
            result.productIdentifier ?? match.pkg?.product?.identifier,
          );
          dispatch(markPremiumOptimistic({ entitlement, targetPlan }));
          dispatch(getEntitlements());
        }

        // Ask the backend to verify this purchase against RevenueCat's REST
        // API and create the canonical Order + MasterClubSubscription
        // immediately, instead of waiting for the asynchronous webhook to
        // land. Idempotent server-side via Order.merchant_transaction_id, so
        // it's safe for both this and the webhook to run. Mirrors what
        // Paywall.handlePurchase does — the Shop widget path is a second IAP
        // entrypoint and needs the same race-closing behavior.
        const reconcileStatus = await reconcilePurchaseWithBackend(result);
        console.log("[ShopWidget IAP] reconcile status", reconcileStatus);

        // Trigger the same downstream refresh path the Stripe widget uses on
        // success: getUserInfo (canonical user state from backend, now
        // guaranteed up-to-date by the reconcile above), calendar cache
        // flush, trial-info invalidation.
        resetUserInfoThrottle();
        globalThis.dispatchEvent(new CustomEvent("shopwidgetnewpurchase"));

        presentToast({ message: "Acquisto completato!", duration: 2500 });
      } catch (err) {
        console.error("[ShopWidget IAP] purchasePackage threw", err);
        presentToast({
          message: "Pagamento non completato. Riprova.",
          duration: 3000,
        });
      }
    };

    globalThis.addEventListener(
      "shopwidgetdigitalpurchaseclicked",
      onDigitalPurchaseClicked,
    );
    return () =>
      globalThis.removeEventListener(
        "shopwidgetdigitalpurchaseclicked",
        onDigitalPurchaseClicked,
      );
  }, [dispatch, presentToast, userId]);

  const notificationCreated = (notification) => {
    void notification;
    dispatch(incrementUnreadQuantity());
  };

  // useSocket({
  //    type: 'NOTIFICATION_CREATED',
  //    callBack: (payload) => {
  //        notificationCreated(payload)
  //    },
  // })

  useEffect(() => {
    if (config.env.mode !== "local") {
      //Make mandatory to do all steps at once
      if (!location.pathname.startsWith("/impostazioni-iniziali/")) return;
      if (location.pathname === "/impostazioni-iniziali/1") return;

      //   if (!history) {
      //     location.href = "/impostazioni-iniziali/1";
      //   }
    }
  }, [history]);

  const inOnboarding = window.location.pathname.startsWith(
    "/impostazioni-iniziali/",
  );

  return (
    <IonApp>
      <ErrorBoundary>
        <IonReactRouter>
          {/* Global app state manager for auto-refresh */}
          <AppStateManager
            enabled={!inOnboarding}
            refreshInterval={1} // Reduced to 1 minute for easier testing
            from_main_group={true}
            group_id={null}
          />
          <AuthBootstrap />
          <AnalyticsScreenTracker />
          <ActivityPingTracker />
          {/* Marks a support session opened from the CRM, and offers the way out. */}
          <ImpersonationBanner />
          <PushNavigationHandler />
          <CommunityDeepLinkHandler />
          <StreamChatBootstrap />
          <RedirectToHome />
          {/* <ClearData /> */}
          {/* <InstallApplicationModal /> */}
          <CoinBalancePoller />
          <CoinTransactionToast />
          {/* Mounted once: an automatic-Story proposal has to outlive the screen
              that raised it — saving a measure closes that screen on the way out. */}
          <StoryPromptSheet />
          {/* Answers the Shop's "Abbonati ora" before the store sheet opens. */}
          <KeyboardAvoidanceManager />
          <MainMenu />
          <RecipeVideoOverlay />
          <NotificationPopup />
          <PushBanner />
          <CommentsSheet />
          <ForceUpdateGate />
          <RealEmailGate />
          <AppleRecoverGate />
          <PermissionGate />
          <PremiumTransitionWatcher />
          <PremiumLiteJourneyIntro />
          <EntitlementPromptHost />
          <Suspense fallback={<LazyFallback />}>
          <IonRouterOutlet id="main-content" swipe-gesture={false}>
            <Switch>
              <RouteAuth path="/" exact={true} component={PreLoginPage} />
              <RouteAuth
                path="/o/:origin"
                exact={true}
                component={PreLoginPage}
              />
              <RouteAuth path="/login" component={LoginPage} exact={true} />
              <Route
                path="/login/:token"
                component={GetTokenPage}
                exact={true}
              />
              {/* The button in the sign-in email (app links and web app) */}
              <Route
                path="/verifica-email"
                component={VerifyEmailLinkPage}
                exact={true}
              />
              <Route
                path="/impersonate/:token"
                component={GetTokenAdminPage}
                exact={true}
              />

              {stepRoutes.map(({ step, WrappedStep }) => (
                <RouteAuth
                  key={step}
                  path={`/impostazioni-iniziali/${step}`}
                  exact
                  component={WrappedStep}
                />
              ))}

              <Redirect
                exact={true}
                from="/impostazioni-iniziali"
                to="/impostazioni-iniziali/1"
              />
              <RouteAuth
                path="/home"
                component={HomeDashboardPage}
                exact={true}
              />

              {/* SHOP ROUTING */}
              <Redirect from="/shop" to="/shopping-widget" exact />

              {/* This single route handles both the base path AND sub-paths
				  because we removed 'exact'. This allows refreshing inside the widget. */}
              <RouteAuth path="/shopping-widget" component={ShopPage} />

              {/* RevenueCat-backed Master Club paywall — native only.
                  Web users hitting this URL are redirected to the shop widget by the page itself. */}
              <RouteAuth path="/paywall" component={PaywallPage} exact={true} />

              {/* In-app Privacy Policy / Terms & Conditions reader. The slug
                  param resolves to a document inside the cached agreements
                  list (`/api/v1/users/agreements/list`). Apple Guideline 3.1.2
                  requires both reachable from the paywall, and Profile/Settings
                  can deep-link here too. */}
              {/* Plain <Route>, NOT <RouteAuth>: legal documents are public
                  by definition (Apple Guideline 3.1.2 requires them
                  reachable from the paywall, where the user may be in a
                  guest-tier or transitional auth state). The previous
                  <RouteAuth> wrapper re-evaluated userAuthStatus() on every
                  navigation and could bounce a guest tap to /login,
                  manifesting as the "click gives error" bug — symptom
                  fixed by making the route auth-agnostic. The page's own
                  agreements fetch still gates the content when the user
                  isn't authenticated. */}
              <Route path="/legal/:slug" component={LegalPage} exact={true} />

              <RouteAuth path="/community" component={HomePage} exact={true} />
              <RouteAuth path="/me" exact component={SocialProfilePage} />
              <RouteAuth
                path="/dieta-social-profile"
                exact
                component={SocialProfilePage}
              />
              <RouteAuth
                path="/u/:userId"
                exact
                component={SocialProfilePage}
              />
              <RouteAuth
                path="/onboarding-complete"
                component={WeHaveFinished}
              />
              {/* <RouteAuth path="/type-of-diet" component={TypeOfDietPage} /> */}
              <RouteAuth path="/followers" exact component={FollowersPage} />
              <RouteAuth path="/following" exact component={FollowingPage} />
              <RouteAuth
                exact
                path="/impostazioni-profilo"
                component={ProfileSettingsPage}
              />

              <RouteAuth
                exact
                path="/utenti-bloccati"
                component={BlockedUsersPage}
              />

              <RouteAuth
                path="/u/:userId/followers"
                exact
                component={FollowersPage}
              />
              <RouteAuth
                path="/u/:userId/following"
                exact
                component={FollowingPage}
              />
              <RouteAuth
                exact
                path="/corso/modulo/:lessonId"
                component={ModuleIntroPage}
              />

              <RouteAuth
                path="/screenlock"
                component={ScreenlockPage}
                exact={true}
              />
              <RouteAuth path="/gruppi" component={GroupsPage} exact={true} />
              <RouteAuth
                path="/gruppi/:groupId"
                component={GroupPage}
                exact={true}
              />
              <RouteAuth
                path="/gruppi/:groupId/:postId"
                component={PostCommentsDeepLink}
                exact={true}
              />
              <RouteAuth path="/chat" component={ChatPage} exact={true} />
              <RouteAuth
                path="/chat/free"
                component={ChatPage}
                exact={true}
              />
              <RouteAuth
                path="/chat/supporto/:supportType"
                component={ChatPage}
                exact={true}
              />
              <RouteAuth
                path="/chat/user/:userId"
                component={ChatPage}
                exact={true}
              />
              <RouteAuth
                path="/nuovo-post"
                component={AddPostPage}
                exact={true}
              />

              <RouteAuth
                path="/gruppi/:groupId/nuovo-post"
                component={AddPostPage}
                exact={true}
              />
              <RouteAuth
                path="/gruppi/:groupId/:postId/:commentId"
                component={CommentThreadDeepLink}
                exact={true}
              />
              <RouteAuth
                path="/multimedia/preferiti"
                component={VideoPreferitiPage}
                exact={true}
              />
              <RouteAuth
                path="/multimedia/categoria/:categoryId"
                component={VideoCategoryPage}
                exact={true}
              />
              <RouteAuth
                path="/multimedia"
                component={MultimediaPage}
                exact={true}
              />
              <RouteAuth
                path="/video/:videoId"
                component={VideoDetailPage}
                exact={true}
              />
              {/* Replay is registered before the detail route so the more
                  specific path wins — a replay plays like a video, it does not
                  open the live event screen. */}
              <RouteAuth
                path="/dirette/:eventId/replay"
                component={StreamingReplayPage}
                exact={true}
              />
              <RouteAuth
                path="/dirette/:eventId"
                component={StreamingEventDetailPage}
                exact={true}
              />
              <Route path="/corso" component={CourseOverviewPage} exact />
              <Route path="/corso/tabs" component={CourseTabsPage} exact />
              {/* Notification from the Tutor: open the next video pill. */}
              <RouteAuth
                path="/corso/riprendi"
                component={CourseResumePage}
                exact={true}
              />
              <RouteAuth
                path="/corso/lezione/:lessonId"
                component={LessonPage}
                exact={true}
              />

              <RouteAuth
                exact
                path="/corso/lezioni/:lessonId"
                component={LessonsListPage}
              />

              <RouteAuth path="/profilo" component={ProfilePage} exact={true} />
              <RouteAuth
                path="/percorso-gratuito"
                component={StartFreeRoutePage}
                exact={true}
              />
              {/* The old checkout pages lead to the Shop / paywall now. */}
              <RouteAuth
                path="/prodotti"
                component={LegacyProductsRedirect}
                exact={true}
              />
              <RouteAuth
                path="/profilo/*"
                component={ProfilePage}
                exact={true}
              />
              <RouteAuth
                path="/aiuto/che-cos-e-dieta-social-app"
                component={WhatIsPage}
                exact={true}
              />
              <RouteAuth
                path="/aiuto/come-utilizzare-dieta-social-app"
                component={HowToUsePage}
                exact={true}
              />
              <RouteAuth
                path="/aiuto/come-funziona-la-prova-gratuita"
                component={HowTrialWorksPage}
                exact={true}
              />
              <RouteAuth
                path="/pagamento"
                component={LegacyPaymentRedirect}
                exact={true}
              />
              <RouteAuth
                path="/metodo-di-pagamento"
                component={PaymentsPage}
                exact={true}
              />
              <RouteAuth
                path="/metodo-di-pagamento/processando"
                component={PaymentMethodProcessingPage}
                exact={true}
              />
              <RouteAuth
                path="/pagamento/successo"
                component={PaymentSuccessPage}
                exact={true}
              />
              <RouteAuth
                path="/pagamento/processando"
                component={PaymentProcessingPage}
                exact={true}
              />
              <Route
                path="/substitution/:type/:cycleId/:itemId"
                component={SubstitutionPage}
                exact={true}
              />
              <RouteAuth
                path="/progressi"
                component={ProgressPage}
                exact={true}
              />
              <RouteAuth
                exact
                path="/il-tuo-percorso/storico"
                component={MioPercorsoStoricoPage}
              />
              <RouteAuth
                exact
                path="/il-tuo-percorso/prima-dopo"
                component={PrimaDopoCreatePage}
              />
              <RouteAuth
                exact
                path="/il-tuo-percorso/:measureSlug"
                component={DiaryMeasurePage}
              />
              <RouteAuth exact path="/il-tuo-percorso" component={DiaryPage} />

              {/* Split personal-area pages (same component, section by route) */}
              <RouteAuth
                exact
                path="/misure-e-progressi"
                component={DiaryPage}
              />
              <RouteAuth
                exact
                path="/preferenze-nutrizionali"
                component={DiaryPage}
              />

              {/* Backward compatible aliases (old percorso route) */}
              <RouteAuth
                exact
                path="/il-mio-percorso/storico"
                component={MioPercorsoStoricoPage}
              />
              <RouteAuth
                exact
                path="/il-mio-percorso/prima-dopo"
                component={PrimaDopoCreatePage}
              />
              <RouteAuth
                exact
                path="/il-mio-percorso/:measureSlug"
                component={DiaryMeasurePage}
              />
              <RouteAuth exact path="/il-mio-percorso" component={DiaryPage} />

              {/* ✅ Backward compatible aliases (old) */}
              <RouteAuth
                exact
                path="/diario/:measureSlug"
                component={DiaryMeasurePage}
              />
              <RouteAuth exact path="/diario" component={DiaryPage} />
              <RouteAuth
                path="/menu/:day/:food/:recipeId"
                component={RecipeMenuPage}
              />
              <RouteAuth
                path="/menu/:day/:food/:recipeId/:cycleId"
                component={RecipeMenuPage}
              />
              <RouteAuth path="/menu/:day" component={MenuPage} exact={true} />
              <RouteAuth path="/menu" component={MenuPage} exact={true} />
              <RouteAuth path="/cibi-non-graditi" component={CibiNonGraditiPage} exact={true} />
              <RouteAuth path="/sfide" component={ChallengesPage} exact={true} />
              <RouteAuth path="/ricette" component={RecipesPage} exact={true} />
              <RouteAuth
                path={"/recipe/:recipeId([1-9]\\d*)"}
                component={RecipePage}
                exact={true}
              />
              <RouteAuth
                path="/ricette/:recipeId"
                component={RecipePage}
                exact={true}
              />
              <RouteAuth
                path="/ricette/:recipeId/:cycleId"
                component={RecipePage}
                exact={true}
              />
              <RouteAuth
                path="/imposta-notifiche"
                component={NotificationsConfigPage}
                exact={true}
              />
              <RouteAuth
                path="/notifiche"
                component={NotificationsPage}
                exact={true}
              />
              <RouteAuth
                path="/saldo-monete"
                component={CoinsBalancePage}
                exact={true}
              />
              <RouteAuth
                path="/storico-monete"
                component={CoinsHistoryPage}
                exact={true}
              />
              <RouteAuth
                path="/regole-monete"
                component={CoinsRulesPage}
                exact={true}
              />
              {/* Area Team — badge-gated staff section (menu visibility is
                  handled in MainMenu; pages are placeholders for now). */}
              <RouteAuth
                path="/area-team/posts"
                component={AreaTeamPostsPage}
                exact={true}
              />
              <RouteAuth
                path="/area-team/crea-post/:postId"
                component={AreaTeamCreatePostsPage}
                exact={true}
              />
              <RouteAuth
                path="/area-team/crea-post"
                component={AreaTeamCreatePostsPage}
                exact={true}
              />
              <RouteAuth
                path="/area-team/gestione-utenti"
                component={AreaTeamGestioneUtentiPage}
                exact={true}
              />
              <RouteAuth
                path="/area-team/chat-supporto/:cid"
                component={AreaTeamChatSupportoDetailPage}
                exact={true}
              />
              <RouteAuth
                path="/area-team/chat-supporto"
                component={AreaTeamChatSupportoPage}
                exact={true}
              />
              <RouteAuth
                path="/area-team/percorso-free"
                component={AreaTeamPercorsoFreePage}
                exact={true}
              />
              <RouteAuth path="/logout" exact={true} component={LogoutRoute} />
              <Route exact={false} component={NotFoundPage} />
            </Switch>
          </IonRouterOutlet>
          </Suspense>
        </IonReactRouter>
      </ErrorBoundary>
    </IonApp>
  );
};

export default App;
